/**
 * What the game's events look like (docs/spec/04 §2, §3): explosions, score pop-ups, sparks, shake
 * and flash. It reads events and never writes the game (05 R3).
 */

import type { EnemyKind, GameState, SimEvent } from '../../sim';
import { FIELD, shipXs } from '../../sim';
import type { Colour } from '../font';
import { BLUE, CYAN, GOLD, GREEN, LIME, MAGENTA, ORANGE, PINK, PURPLE, RED, SKY, WHITE, YELLOW } from '../palette';
import type { Screen } from '../screen';
import { CAPSULE_LOOK, drawSprite } from '../sprites';
import { Particles } from './particles';

interface Boom {
  x: number;
  y: number;
  t: number;
  big: boolean;
}

interface Popup {
  x: number;
  y: number;
  t: number;
  text: string;
  colour: Colour;
}

export interface FxOptions {
  readonly particles: boolean;
  readonly shake: boolean;
  readonly reduceFlashing: boolean;
}

const KIND_COLOURS: Readonly<Record<EnemyKind, readonly number[]>> = {
  stinger: [YELLOW, BLUE, CYAN],
  moth: [RED, WHITE, SKY],
  monarch: [GREEN, GOLD, YELLOW],
  captive: [RED, WHITE],
  morph: [ORANGE, LIME, MAGENTA],
};

/** Ticks per explosion frame. */
const BOOM_STEP = 3;
const BIG_STEP = 5;
const POPUP_TICKS = 55;

export class Fx {
  readonly particles = new Particles();
  private booms: Boom[] = [];
  private popups: Popup[] = [];
  /** Ticks of white flash left. */
  private flash = 0;

  clear(): void {
    this.particles.clear();
    this.booms = [];
    this.popups = [];
    this.flash = 0;
  }

  private popup(x: number, y: number, text: string, colour: Colour): void {
    this.popups.push({ x: Math.min(FIELD.w - 20, Math.max(20, x)), y, t: 0, text, colour });
  }

  /** One tick's events. */
  events(events: readonly SimEvent[], o: FxOptions): void {
    const ps = this.particles;
    for (const e of events) {
      switch (e.type) {
        case 'enemyKilled': {
          this.booms.push({ x: e.x, y: e.y, t: 0, big: false });
          if (o.particles) for (const c of KIND_COLOURS[e.kind]) ps.burst(e.x, e.y, 6, c, 1.9, 26, 0.02);
          if (e.popup) this.popup(e.x, e.y, String(e.points), e.kind === 'captive' ? RED : CYAN);
          break;
        }
        case 'enemyHit':
          if (o.particles) ps.burst(e.x, e.y, 8, PURPLE, 1.6, 16);
          break;
        case 'playerDied':
          this.booms.push({ x: e.x, y: e.y, t: 0, big: true });
          if (o.particles) {
            ps.burst(e.x, e.y, 34, WHITE, 2.8, 44, 0.02);
            ps.burst(e.x, e.y, 22, RED, 2.2, 54, 0.03);
            ps.burst(e.x, e.y, 16, SKY, 1.6, 60, 0.03);
          }
          if (o.shake) ps.shake(3, 22);
          break;
        case 'waveBonus':
        case 'morphBonus':
          this.popup(e.x, e.y, String(e.points), YELLOW);
          break;
        case 'powerUp':
          this.popup(e.x, e.y - 10, CAPSULE_LOOK[e.kind].name, CAPSULE_LOOK[e.kind].colour);
          if (o.particles) ps.burst(e.x, e.y, 18, CAPSULE_LOOK[e.kind].colour, 2, 28);
          break;
        case 'shieldBurst':
          if (o.particles) ps.burst(e.x, e.y, 26, SKY, 2.6, 30);
          if (o.shake) ps.shake(2, 8);
          break;
        case 'nova':
          if (o.particles) {
            for (let i = 0; i < 4; i++) ps.burst(e.x, e.y - 40 * i, 30, i % 2 === 0 ? CYAN : WHITE, 4.5, 36);
          }
          if (o.shake) ps.shake(3, 16);
          if (!o.reduceFlashing) this.flash = 4;
          break;
        case 'docked':
          if (o.particles) ps.burst(e.x, e.y, 14, WHITE, 1.4, 22);
          break;
        case 'empressHit':
          if (o.particles) ps.burst(e.x + (ps.rand() * 40 - 20), e.y + 10, 4, PINK, 1.6, 18, 0.04);
          break;
        case 'empressPhase':
          if (o.shake) ps.shake(3, 30);
          break;
        case 'empressBlast':
          this.booms.push({ x: e.x, y: e.y, t: 0, big: ps.rand() < 0.4 });
          if (o.particles) ps.burst(e.x, e.y, 14, ps.rand() < 0.5 ? MAGENTA : YELLOW, 2.4, 40, 0.03);
          if (o.shake) ps.shake(2, 10);
          break;
        case 'empressKilled':
          this.popup(e.x, e.y, String(e.points), YELLOW);
          if (o.shake) ps.shake(4, 40);
          if (!o.reduceFlashing) this.flash = 5;
          break;
        default:
      }
    }
  }

  /** Move on one tick; the Lancer leaves exhaust, capsules glitter. */
  update(s: GameState, o: FxOptions): void {
    const ps = this.particles;
    ps.update();
    if (this.flash > 0) this.flash--;
    this.booms = this.booms.filter((b) => ++b.t < (b.big ? 6 * BIG_STEP : 5 * BOOM_STEP));
    this.popups = this.popups.filter((p) => ++p.t < POPUP_TICKS);
    if (!o.particles) return;
    const p = s.player;
    if ((p.status === 'flying' || p.status === 'arriving') && s.tick % 2 === 0) {
      for (const x of shipXs(s)) {
        ps.spark(x - 2 + ps.rand() * 4, FIELD.fighterY + 9, (ps.rand() - 0.5) * 0.3, 0.7 + ps.rand() * 0.6, ps.rand() < 0.5 ? ORANGE : YELLOW, 8);
      }
    }
    for (const c of s.capsules) {
      if (s.tick % 5 === 0) ps.spark(c.x - 6 + ps.rand() * 12, c.y - 6, 0, -0.2, CAPSULE_LOOK[c.kind].colour, 12);
    }
  }

  shakeOffset(): { x: number; y: number } {
    return this.particles.offset();
  }

  draw(screen: Screen): void {
    for (const b of this.booms) {
      if (b.big) drawSprite(screen, 'bigBoom', Math.floor(b.t / BIG_STEP), b.x, b.y);
      else drawSprite(screen, 'boom', Math.floor(b.t / BOOM_STEP), b.x, b.y);
    }
    this.particles.draw(screen);
    for (const p of this.popups) {
      // Pop-ups rise a little and blink out at the end.
      if (p.t > POPUP_TICKS - 12 && (p.t & 2) === 0) continue;
      screen.shadowText(p.text, p.x, p.y - 4 - Math.min(10, p.t / 3), p.colour, 0, 'center');
    }
    if (this.flash > 0) screen.faded(0.75, () => screen.fill(0, 0, screen.width, screen.height, WHITE));
  }
}
